/**
 * Normalized Loading State Utilities
 * 
 * Provides consistent loading state management across Redux slices.
 * Eliminates scattered loading flags and creates predictable loading patterns.
 * 
 * Features:
 * - Centralized loading state structure
 * - Operation-specific loading tracking
 * - Loading state helpers and selectors
 * - Consistent error handling with loading states
 */

/**
 * Standard loading state structure for Redux slices
 * 
 * @typedef {Object} LoadingState
 * @property {boolean} idle - No operations running
 * @property {Object} operations - Operation-specific loading flags
 * @property {string|null} error - Global error message
 * @property {Object} errors - Operation-specific errors
 */

/**
 * Creates initial loading state structure
 * 
 * @param {string[]} operations - List of operation names to track
 * @returns {LoadingState} Initial loading state
 */
export const createInitialLoadingState = (operations = []) => {
  const state = {
    idle: true,
    operations: {},
    error: null,
    errors: {}
  };

  // Initialize operation-specific loading flags
  operations.forEach(operation => {
    state.operations[operation] = false;
    state.errors[operation] = null;
  });

  return state;
};

/**
 * Standard operations for common Redux patterns
 */
export const STANDARD_OPERATIONS = {
  FETCH: 'fetch',
  FETCH_ALL: 'fetchAll',
  FETCH_BY_ID: 'fetchById',
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  ADD: 'add',
  REMOVE: 'remove'
};

/**
 * Loading state reducers for common async patterns
 */
export const loadingStateReducers = {
  /**
   * Sets loading state for pending async action
   * 
   * @param {Object} state - Redux state
   * @param {string} operation - Operation name
   */
  setPending: (state, operation) => {
    state.operations[operation] = true;
    state.errors[operation] = null;
    state.error = null;
    state.idle = false;
  },

  /**
   * Sets success state for fulfilled async action
   * 
   * @param {Object} state - Redux state
   * @param {string} operation - Operation name
   */
  setFulfilled: (state, operation) => {
    state.operations[operation] = false;
    state.errors[operation] = null;
    
    // Check if all operations are complete
    const stillLoading = Object.values(state.operations).some(loading => loading);
    state.idle = !stillLoading;
  },

  /**
   * Sets error state for rejected async action
   * 
   * @param {Object} state - Redux state
   * @param {string} operation - Operation name
   * @param {string} errorMessage - Error message
   */
  setRejected: (state, operation, errorMessage) => {
    state.operations[operation] = false;
    state.errors[operation] = errorMessage;
    state.error = errorMessage; // Also set global error
    
    // Check if all operations are complete
    const stillLoading = Object.values(state.operations).some(loading => loading);
    state.idle = !stillLoading;
  },

  /**
   * Clears specific operation error
   * 
   * @param {Object} state - Redux state
   * @param {string} operation - Operation name
   */
  clearOperationError: (state, operation) => {
    state.errors[operation] = null;
    
    // Clear global error if no operation-specific errors remain
    const hasErrors = Object.values(state.errors).some(error => error !== null);
    if (!hasErrors) {
      state.error = null;
    }
  },

  /**
   * Clears all errors
   * 
   * @param {Object} state - Redux state
   */
  clearAllErrors: (state) => {
    state.error = null;
    Object.keys(state.errors).forEach(operation => {
      state.errors[operation] = null;
    });
  }
};

/**
 * Creates standardized extra reducers for async thunks
 * 
 * @param {Object} builder - Redux Toolkit builder
 * @param {Object} asyncThunk - Async thunk object
 * @param {string} operation - Operation name
 * @param {Object} options - Additional options
 * @param {Function} options.onFulfilled - Custom fulfilled handler
 * @param {Function} options.onRejected - Custom rejected handler
 */
export const createAsyncReducers = (builder, asyncThunk, operation, options = {}) => {
  const { onFulfilled, onRejected } = options;

  builder
    .addCase(asyncThunk.pending, (state) => {
      loadingStateReducers.setPending(state, operation);
    })
    .addCase(asyncThunk.fulfilled, (state, action) => {
      loadingStateReducers.setFulfilled(state, operation);
      if (onFulfilled) {
        onFulfilled(state, action);
      }
    })
    .addCase(asyncThunk.rejected, (state, action) => {
      const errorMessage = action.payload || action.error?.message || 'An error occurred';
      loadingStateReducers.setRejected(state, operation, errorMessage);
      if (onRejected) {
        onRejected(state, action);
      }
    });
};

/**
 * Selectors for loading states
 */
export const createLoadingSelectors = (sliceName) => ({
  /**
   * Checks if any operation is loading
   */
  selectIsLoading: (state) => !state[sliceName].idle,

  /**
   * Checks if specific operation is loading
   */
  selectIsOperationLoading: (operation) => (state) => 
    state[sliceName].operations[operation] || false,

  /**
   * Gets global error
   */
  selectError: (state) => state[sliceName].error,

  /**
   * Gets operation-specific error
   */
  selectOperationError: (operation) => (state) => 
    state[sliceName].errors[operation],

  /**
   * Gets all loading operations
   */
  selectLoadingOperations: (state) => 
    Object.keys(state[sliceName].operations).filter(
      operation => state[sliceName].operations[operation]
    ),

  /**
   * Gets loading state summary
   */
  selectLoadingSummary: (state) => ({
    idle: state[sliceName].idle,
    hasError: Boolean(state[sliceName].error),
    loadingCount: Object.values(state[sliceName].operations).filter(Boolean).length,
    errorCount: Object.values(state[sliceName].errors).filter(Boolean).length
  })
});

/**
 * React hook for loading states (to be used with useSelector)
 * 
 * @param {string} sliceName - Redux slice name
 * @param {string} operation - Operation name
 * @returns {Object} Loading state object
 */
export const createUseLoadingHook = (sliceName, operation) => () => {
  const selectors = createLoadingSelectors(sliceName);
  
  return {
    isLoading: selectors.selectIsOperationLoading(operation),
    error: selectors.selectOperationError(operation),
    isIdle: (state) => state[sliceName].idle,
    clearError: () => ({ type: `${sliceName}/clearOperationError`, payload: operation })
  };
};

/**
 * Enhanced loading state for complex operations
 * 
 * Supports sub-operations and progress tracking
 */
export const createEnhancedLoadingState = (operations = [], subOperations = {}) => {
  const state = createInitialLoadingState(operations);
  
  // Add sub-operation tracking
  state.subOperations = {};
  state.progress = {};
  
  Object.keys(subOperations).forEach(operation => {
    state.subOperations[operation] = {};
    state.progress[operation] = 0;
    
    subOperations[operation].forEach(subOp => {
      state.subOperations[operation][subOp] = false;
    });
  });
  
  return state;
};

/**
 * Progress tracking utilities
 */
export const progressReducers = {
  /**
   * Sets progress for an operation
   * 
   * @param {Object} state - Redux state
   * @param {string} operation - Operation name
   * @param {number} progress - Progress percentage (0-100)
   */
  setProgress: (state, operation, progress) => {
    state.progress[operation] = Math.max(0, Math.min(100, progress));
  },

  /**
   * Increments progress for an operation
   * 
   * @param {Object} state - Redux state
   * @param {string} operation - Operation name
   * @param {number} increment - Progress increment
   */
  incrementProgress: (state, operation, increment = 10) => {
    const current = state.progress[operation] || 0;
    state.progress[operation] = Math.min(100, current + increment);
  },

  /**
   * Completes progress for an operation
   * 
   * @param {Object} state - Redux state
   * @param {string} operation - Operation name
   */
  completeProgress: (state, operation) => {
    state.progress[operation] = 100;
  },

  /**
   * Resets progress for an operation
   * 
   * @param {Object} state - Redux state
   * @param {string} operation - Operation name
   */
  resetProgress: (state, operation) => {
    state.progress[operation] = 0;
  }
};

/**
 * Component loading states for UI consistency
 */
export const UI_LOADING_STATES = {
  SKELETON: 'skeleton',
  SPINNER: 'spinner',
  PROGRESS: 'progress',
  DISABLED: 'disabled',
  SHIMMER: 'shimmer'
};

/**
 * Creates loading state props for components
 * 
 * @param {Object} loadingState - Current loading state
 * @param {string} operation - Operation name
 * @returns {Object} Component loading props
 */
export const createLoadingProps = (loadingState, operation) => ({
  isLoading: loadingState.operations[operation] || false,
  error: loadingState.errors[operation],
  hasError: Boolean(loadingState.errors[operation]),
  canRetry: Boolean(loadingState.errors[operation]),
  loadingType: loadingState.operations[operation] ? UI_LOADING_STATES.SPINNER : null
});

export default {
  createInitialLoadingState,
  loadingStateReducers,
  createAsyncReducers,
  createLoadingSelectors,
  STANDARD_OPERATIONS,
  UI_LOADING_STATES
};